import React, { useEffect, useState } from 'react';

import { getTrackDetailPageUrls } from 'apps/track-details/utils/trackDetailsLinks';
import useConfigurations from 'hooks/useConfigurations';
import UnderwritingDetails from './UnderwritingDetails';
import { formatBeginningHomeValuationExpStatus } from './helper';
import { HomeResidenceTypes } from './types/types';
import { Configurations } from '../LiensAndPaydownsController/types/types';
import { BROADCAST_CHANNELS, useBroadcastChannel } from 'hooks/useBroadcastChannel';

import './UnderwritingDetailsController.scss';

type UnderwritingDetailsControllerProps = {
  track: any;
  refetch: () => void;
  refetchFinalApprovalBlockingErrors: () => void;
  enabledFinalApprovalBlockingErrors: boolean;
  finalApprovalBlockingErrors?: string[];
  refetchFinalApprovalBlockingErrorsData?: () => void;
};

export const UnderwritingDetailsController = ({
  track: initialTrack,
  refetch,
  refetchFinalApprovalBlockingErrors,
  enabledFinalApprovalBlockingErrors,
  finalApprovalBlockingErrors,
  refetchFinalApprovalBlockingErrorsData,
}: UnderwritingDetailsControllerProps) => {
  const { uwRuleVersion } = useConfigurations() as Configurations;
  const [track, setTrack] = useState(initialTrack);

  useBroadcastChannel({
    channelName: BROADCAST_CHANNELS.UNDERWRITING_CALCULATOR_SAVED,
    onMessage: message => {
      // Update with the most recent track data from the uw calculator
      if (message?.trackId === track?.identifier) {
        setTrack({ ...track, ...message?.track });
        refetchFinalApprovalBlockingErrors();
      }
    },
  });

  useEffect(() => {
    setTrack(initialTrack);
  }, [initialTrack]);

  const { trackHistoryUrl } = getTrackDetailPageUrls(track.identifier);

  return (
    <UnderwritingDetails
      trackId={track.identifier}
      trackHistoryUrl={trackHistoryUrl}
      uwRuleVersion={uwRuleVersion}
      applicationSpecialist={track.coordinator}
      underwriter={track.underwriter}
      htLienPosition={track.htLienPosition}
      principalPaydown={track.principalPaydown}
      totalPaydown={track.totalPaydown}
      maximumInvestmentAmount={track.maxInvestmentAmount}
      beginningHomeValuation={track.beginningHomeValuation?.value ?? track.relevantHomeValuation?.value}
      beginningHomeValuationExpStatus={formatBeginningHomeValuationExpStatus(track.beginningHomeValuation)}
      beginningHomeValuationLabel={
        track.beginningHomeValuation ? 'Beginning Home Valuation' : 'Relevant Home Valuation'
      }
      lienBalanceTotal={track.currentLienBalanceTotal}
      pastDueLienBalanceTotal={track.currentPastDueLienBalanceTotal}
      ownersEndingEquity={track.ownerEndingEquityPercent}
      totalEndingEquity={track.totalEndingEquityPercent}
      lossThreshold={track.latestUwCalculatorOutput?.lossThreshold}
      trackReviewStatus={track.reviewStatus?.value}
      currentIncomeWeightedRiskBand={track.incomeWeightedRiskBand?.value}
      finalCompositeFico={track.compositeFicoData?.value}
      shareUp={track.latestUwCalculatorOutput?.hometapUpshare}
      shareDown={track.latestUwCalculatorOutput?.hometapDownshare}
      hasRentalResidentialType={track.home?.residenceType === HomeResidenceTypes.RESIDENCE_TYPE_RENTAL}
      rb6Criteria={track?.rb6Criteria}
      highestSingleYearDelinquentTaxes={track?.highestSingleYearDelinquentTaxes}
      uwDscr={track.uwDscr}
      refetch={refetch}
      applicationIsApprovedOrClosedLost={Boolean(track.applicationIsApprovedOrClosedLost)}
      latestUwCalculatorOutput={track.latestUwCalculatorOutput}
      finalApprovalBlockingErrors={finalApprovalBlockingErrors}
      enabledFinalApprovalBlockingErrors={enabledFinalApprovalBlockingErrors}
      refetchFinalApprovalBlockingErrorsData={refetchFinalApprovalBlockingErrorsData}
      thirdLienCap={track.thirdLienCap}
    />
  );
};
